'use client';

import Link from 'next/link';
import { SafeHtmlRenderer } from '@/components/ui';
import { tiptapToHtml, formatDateTime, formatDate, formatFileSize } from '@/utils';
import type { BoardCategory, BoardPost, PostContent } from '@/types/board';
import Attachment from '@public/svgs/attachment.svg'
import { PERIOD_CATEGORIES } from '@/constants/board';

interface BoardPostProps {
  category: BoardCategory;
  post: BoardPost;
}

export function BoardPost({ category, post }: BoardPostProps) {
  const needsPeriodColumn = PERIOD_CATEGORIES.includes(category.id as 'recruit' | 'rise-projects');

  // TipTap 구조화된 콘텐츠를 렌더링하는 함수
  const renderTipTapContent = (content: PostContent) => {
    if (!content.content || !Array.isArray(content.content)) {
      return <p className="text-slate-500 text-center py-12">게시물 내용이 없습니다.</p>;
    }

    // tiptapToHtml 함수를 사용하여 HTML로 변환하고 SafeHtmlRenderer로 안전하게 렌더링
    const htmlContent = tiptapToHtml(content);
    return (
      <SafeHtmlRenderer
        html={htmlContent}
      />
    );
  };

  // 콘텐츠를 렌더링하는 메인 함수
  const renderContent = (content: string | PostContent | undefined) => {
    if (!content) {
      return <p className="text-slate-500 text-center py-12">게시물 내용이 없습니다.</p>;
    }

    // JSON 문자열인 경우 파싱 시도
    if (typeof content === 'string') {
      try {
        // JSON 문자열인지 확인하고 파싱
        const parsedContent = JSON.parse(content);

        // TipTap 형식인지 확인 (type이 'doc'이고 content 배열이 있는 경우)
        if (parsedContent.type === 'doc' && parsedContent.content) {
          return renderTipTapContent(parsedContent);
        }

        // 파싱된 내용이 유효하지 않은 경우 원본 텍스트로 표시 (줄바꿈 유지)
        return (
          <p className="mb-6 text-slate-700 leading-relaxed text-base sm:text-lg whitespace-pre-wrap">
            {content}
          </p>
        );
      } catch (error) {
        // JSON 파싱 실패 시 일반 텍스트로 표시
        return (
          <p className="mb-6 text-slate-700 leading-relaxed text-base sm:text-lg whitespace-pre-wrap">
            {content}
          </p>
        );
      }
    }

    // 이미 객체인 경우
    // TipTap 형식인지 확인 (type이 'doc'이고 content 배열이 있는 경우)
    if (content.type === 'doc' && content.content) {
      return renderTipTapContent(content);
    }

    return <p className="text-slate-500 text-center py-12">게시물 내용을 불러올 수 없습니다.</p>;
  };

  const getListPath = () => {
    switch (category.id) {
      case 'notices':
        return '/notices';
      case 'recruit':
        return '/recruit';
      case 'rise-projects':
        return '/announcements';
      case 'resources':
        return '/resources';
      case 'living-research':
        return '/organization?department=living-research';
      default:
        return '/notices';
    }
  };

  const getPostDetailPath = (postId: string) => {
    switch (category.id) {
      case 'notices':
        return `/notices/${postId}`;
      case 'recruit':
        return `/recruit/${postId}`;
      case 'rise-projects':
        return `/announcements/${postId}`;
      case 'resources':
        return `/resources/${postId}`;
      case 'living-research':
        return `/living-research/${postId}`;
      default:
        return `/notices/${postId}`;
    }
  };

  return (
    <div className="w-full mx-auto bg-white">
      <header className="mb-6 sm:mb-8 bg-background sm:bg-gray-50 rounded-none sm:rounded-2xl border-0 border-b-2 sm:border-b-1 sm:border border-foreground/30 sm:border-border overflow-hidden">
        <div className="p-0 pb-6 sm:pb-8 sm:p-8">
          <div className="mb-3 sm:mb-4">
            <span className="inline-block px-2 sm:px-3 py-1 bg-primary text-white text-xs sm:text-sm font-medium rounded-full">
              {category.label}
            </span>
          </div>

          <h1 className="text-xl sm:text-2xl lg:text-3xl xl:text-4xl font-bold text-foreground mb-4 sm:mb-6 leading-tight">
            {category.id === 'resources' && post.category ? (
              <span className="inline-block group-hover:underline">
                <span className="inline-block font-bold text-primary mr-2">
                  [{post.category}]
                </span>
                {post.title}
              </span>
            ) : (
              post.title
            )}
          </h1>

          <div className="flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-center gap-3 sm:gap-4 lg:gap-6 text-sm sm:text-base font-semibold text-muted">
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              <span>{post.author}</span>
            </div>
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span>작성일: {formatDateTime(post.createdAt)}</span>
            </div>
            {needsPeriodColumn && (
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4 sm:w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>
                  {post.startDate && post.endDate ? (
                    `공고기간: ${formatDate(post.startDate)} ~ ${formatDate(post.endDate)}`
                  ) : (
                    '공고기간 없음'
                  )}
                </span>
              </div>
            )}
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
              <span>{post.views.toLocaleString()}</span>
            </div>
          </div>
        </div>
      </header>

      <main className="mb-8 sm:mb-10 lg:mb-12">
        <article className="bg-white rounded-xl sm:rounded-2xl border-0 border-border sm:border-1 overflow-hidden">
          <div className="p-0 sm:p-8 min-h-[400px] sm:min-h-[500px] leading-relaxed prose prose-sm sm:prose-base lg:prose-lg max-w-none">
            {renderContent(post.content)}
          </div>
        </article>
      </main>

      {post.attachments && post.attachments.length > 0 && (
        <section className="mb-8 sm:mb-10 lg:mb-12">
          <div className="bg-white rounded-xl sm:rounded-2xl border border-border overflow-hidden">
            <div className="p-4 sm:p-6 border-b border-border bg-gray-50">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="w-2 h-4 sm:h-6 bg-primary rounded-full" />
                <h3 className="text-lg sm:text-xl font-semibold text-foreground">첨부파일</h3>
                <span className="px-2 py-1 bg-primary text-white text-xs sm:text-sm font-medium rounded-full">
                  {post.attachments.length}개
                </span>
              </div>
            </div>
            <div className="p-4 sm:p-6">
              <div className="space-y-3 sm:space-y-4">
                {post.attachments.map((attachment) => (
                  <div
                    key={attachment.id}
                    className="flex flex-col sm:flex-row sm:items-center sm:justify-between border-b pb-3 sm:pb-4 border-border last:border-b-0 last:pb-0 gap-3 sm:gap-4"
                  >
                    <div className="flex items-center gap-3 sm:gap-4">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gray-50 border border-border rounded-lg flex items-center justify-center">
                        <Attachment className="w-5 h-5 sm:w-6 sm:h-6 text-foreground" />
                      </div>
                      <div>
                        <p className="font-semibold text-foreground mb-1 text-sm sm:text-base">
                          {attachment.originalName}
                        </p>
                        <p className="text-xs sm:text-sm text-muted">
                          {formatFileSize(attachment.fileSize)}
                        </p>
                      </div>
                    </div>
                    <a
                      href={`/api/attachments/${attachment.id}`}
                      download={attachment.originalName}
                      className="px-4 sm:px-5 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-all duration-100 text-sm sm:text-base font-medium shadow-sm hover:shadow-md text-center"
                    >
                      다운로드
                    </a>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      <nav className="mb-8 sm:mb-10 lg:mb-12">
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
          {post.prevPost ? (
            <Link
              href={getPostDetailPath(post.prevPost.id)}
              className={`group flex items-center gap-3 sm:gap-4 p-3 sm:p-4 rounded-lg sm:rounded-xl border border-border hover:bg-gray-50 transition-all duration-100 ${post.nextPost ? 'md:flex-1' : 'md:w-1/2'}`}
            >
              <div className="hidden sm:flex w-9 h-9 sm:w-11 sm:h-11 bg-secondary/10 rounded-full items-center justify-center group-hover:bg-secondary/20 transition-colors duration-100">
                <svg
                  className="w-5 h-5 sm:w-6 sm:h-6 text-foreground"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-muted">이전 글</p>
                <p className="text-base text-foreground font-semibold line-clamp-1">
                  {post.prevPost.title}
                </p>
              </div>
            </Link>
          ) : (
            <div className={`flex items-center gap-3 sm:gap-4 p-3 sm:p-4 rounded-lg sm:rounded-xl border border-border bg-gray-50 ${post.nextPost ? 'md:flex-1' : 'md:w-1/2'}`}>
              <div className="hidden sm:flex w-9 h-9 sm:w-11 sm:h-11 bg-gray-200 rounded-full items-center justify-center">
                <svg
                  className="w-5 h-5 sm:w-6 sm:h-6 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-muted">이전 글</p>
                <p className="text-base text-gray-400 font-semibold">
                  이전글이 없습니다
                </p>
              </div>
            </div>
          )}
          {post.nextPost ? (
            <Link
              href={getPostDetailPath(post.nextPost.id)}
              className={`group flex items-center gap-3 sm:gap-4 p-3 sm:p-4 rounded-lg sm:rounded-xl border border-border hover:bg-gray-50 transition-all duration-100 ${post.prevPost ? 'md:flex-1' : 'md:w-1/2 md:ml-auto'}`}
            >
              <div className="flex-1 min-w-0 text-left sm:text-right">
                <p className="text-sm text-muted">다음 글</p>
                <p className="text-base text-foreground font-semibold line-clamp-1">
                  {post.nextPost.title}
                </p>
              </div>
              <div className="hidden sm:flex w-9 h-9 sm:w-11 sm:h-11 bg-secondary/10 rounded-full items-center justify-center group-hover:bg-secondary/20 transition-colors duration-100">
                <svg
                  className="w-5 h-5 sm:w-6 sm:h-6 text-foreground"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </div>
            </Link>
          ) : (
            <div className={`flex items-center gap-3 sm:gap-4 p-3 sm:p-4 rounded-lg sm:rounded-xl border border-border bg-gray-50 ${post.prevPost ? 'md:flex-1' : 'md:w-1/2 md:ml-auto'}`}>
              <div className="flex-1 min-w-0 text-left sm:text-right">
                <p className="text-sm text-muted">다음 글</p>
                <p className="text-base text-gray-400 font-semibold">
                  다음글이 없습니다
                </p>
              </div>
              <div className="hidden sm:flex w-9 h-9 sm:w-11 sm:h-11 bg-gray-200 rounded-full items-center justify-center">
                <svg
                  className="w-5 h-5 sm:w-6 sm:h-6 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </div>
            </div>
          )}
        </div>
      </nav>

      <div className="flex justify-center">
        <Link
          href={getListPath()}
          className="inline-flex items-center gap-2 px-6 sm:px-8 py-3 sm:py-4 bg-slate-800 text-white rounded-lg sm:rounded-xl hover:bg-slate-700 transition-all duration-100 font-medium text-sm sm:text-base"
        >
          <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          목록으로 돌아가기
        </Link>
      </div>
    </div>
  );
} 
